import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import {
  BarChart,
  Bar,
  XAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

import { Card, CardContent } from '@/components/ui/card';
import { ImageLightbox } from '@/components/ui/image-lightbox';
import { Clock, Loader2, Sunrise, Users, UsersRound, Hourglass } from 'lucide-react';
import { MetricBig, MetricCard, MetricRow, MetricSmall } from '@/components/report/MetricCard';
import { cn } from '@/lib/utils';
import { useT } from '@/contexts/I18nContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { DayQueueCard } from '@/components/report/day/DayQueueCard';
import { StaffArrivalSummaryCard } from '@/components/report/day/StaffArrivalCard';
import { Link } from 'react-router-dom';
import { getQueuesStaffSettings, queuesStaffKey } from '@/api/queuesStaff';
import {
  getQueueDetail,
  getQueueWaitSummary,
  getQueueWorkerExamples,
  getStaffArrival,
  getFrameBoxes,
  type QueueArea,
  type QueueAreaHistogram,
  type QueueDetailResponse,
  type QueueDwellCell,
  type QueueHistogramBucket,
} from '@/api/analytics';

function fmtMinutes(totalMin: number): string {
  if (totalMin <= 0) return '0 мин';
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m} мин`;
  if (m === 0) return `${h} ч`;
  return `${h} ч ${m} мин`;
}

// Area colors match the queue chart's series: очередь amber, кухня emerald,
// ожидают indigo.
const AREA_COLORS: Record<QueueArea, string> = {
  queue: '#f59e0b',
  kitchen: '#10b981',
  waiting: '#6366f1',
};

type WorkerMatrixMode = 'share' | 'perDay' | 'total';

/** Минуты в клетке «в день»: дробные — с одним знаком, чтобы 0.4 не стало 0. */
function fmtCellMin(v: number): string {
  if (v >= 10) return String(Math.round(v));
  const r = Math.round(v * 10) / 10;
  return r > 0 ? String(r) : '<0.1';
}

function WorkerMatrix({
  cells,
  label,
  kitchenLabel,
  frontLabel,
  zoneId,
  from,
  to,
  perDayDivisor,
}: {
  cells: QueueDetailResponse['worker_matrix'];
  label: string;
  kitchenLabel: string;
  frontLabel: string;
  zoneId: string;
  from: string;
  to: string;
  /** Effective days in the range — minutes are shown per day on multi-day. */
  perDayDivisor: number;
}) {
  const { t } = useT();
  // Как читать клетку (Алексей #3383): «доля в столбце» — какой состав бара
  // был при N гостях у кассы (иначе всё забивает столбец «0 гостей»);
  // «мин в день» — сравнивать периоды разной длины; «мин за период» — сырое.
  // На одном дне «в день» = «за период», поэтому там только доля / минуты.
  const multiDay = perDayDivisor > 1.2;
  const [mode, setMode] = useState<WorkerMatrixMode>(() => {
    try {
      const v = localStorage.getItem('queue.workerMatrix.mode');
      if (v === 'share' || v === 'perDay' || v === 'total') return v;
    } catch {
      /* private mode */
    }
    return 'share';
  });
  const pickMode = (m: WorkerMatrixMode) => {
    setMode(m);
    try {
      localStorage.setItem('queue.workerMatrix.mode', m);
    } catch {
      /* private mode */
    }
  };
  const effMode: WorkerMatrixMode = mode === 'perDay' && !multiDay ? 'total' : mode;
  // Time-weighted average crew-to-guests ratio, stated the way a manager would
  // ask it: "how many guests per person on the kitchen" (Alexey 2026-08-11 —
  // «стоит добавить тоже метрику среднее соотношение, прямо в ту же таблицу»).
  // Cells with an empty kitchen are excluded: dividing by zero crew would give
  // infinity, and "guests per nobody" is not a staffing ratio.
  const avgRatio = useMemo(() => {
    let minutes = 0;
    let guests = 0;
    let crew = 0;
    for (const c of cells) {
      if (c.kitchen <= 0) continue;
      minutes += c.minutes;
      guests += c.front_of_register * c.minutes;
      crew += c.kitchen * c.minutes;
    }
    if (minutes <= 0 || crew <= 0) return null;
    return { perWorker: guests / crew, avgCrew: crew / minutes, avgGuests: guests / minutes };
  }, [cells]);

  const [hover, setHover] = useState<{ x: number; y: number; row: number; col: number; m: number } | null>(null);
  // Clicked cell → a gallery of example frames from those exact minutes.
  const [examplesCell, setExamplesCell] = useState<{ row: number; col: number } | null>(null);
  const [exampleIdx, setExampleIdx] = useState(0);
  // Ошибка запроса раньше не показывалась вовсе: «Ищем примеры…» исчезало, и всё
  // (Алексей 04.11 #4083) — теперь окно остаётся с текстом ошибки; повтор — кликом.
  const { data: examples, isLoading: examplesLoading, error: examplesError } = useSWR(
    examplesCell ? ['queue-worker-examples', zoneId, from, to, examplesCell.row, examplesCell.col] : null,
    () =>
      getQueueWorkerExamples(zoneId, from, to, examplesCell!.row, examplesCell!.col).then(
        (r) => r.data.examples,
      ),
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  // «рисовать боксы» (#158) — staff-only debug overlay showing raw detected
  // boxes with their center + feet points, so it's obvious what the model
  // actually saw vs. what counted as "in" the polygon. Gated the same way the
  // backend gates the endpoint: is_staff/is_superuser, nothing zone-scoped.
  //
  // 09.09: the checkbox used to live in this panel's header, permanently, even
  // though it only affects the example-frames lightbox that opens on a cell
  // click — a control for a modal that isn't open, parked next to the title
  // (Alexey: «кнопку рисовать боксы… она там совершенно рандомно стоит»).
  // Removed from the header; the debug overlay itself stays reachable for
  // staff via ?boxes=1 on the URL so #158 isn't silently lost.
  const { user } = useCurrentUser();
  const isStaff = user?.is_staff === true || user?.is_superuser === true;
  const showBoxes =
    isStaff && new URLSearchParams(window.location.search).get('boxes') === '1';
  const frameIds = examples?.map((e) => e.frame_id) ?? [];
  const { data: boxesByFrame } = useSWR(
    showBoxes && frameIds.length ? ['frame-boxes', ...frameIds] : null,
    () => getFrameBoxes(frameIds).then((r) => r.data.boxes_by_frame),
    { revalidateOnFocus: false },
  );
  // The gallery must not open (and freeze its props) until boxes are in hand.
  const boxesPending = showBoxes && frameIds.length > 0 && !boxesByFrame;
  const { rows, cols, maxMinutes, minutesByKey, colTotal, colMax } = useMemo(() => {
    const kSet = new Set<number>();
    const fSet = new Set<number>();
    const map = new Map<string, number>();
    const colTotal = new Map<number, number>();
    const colMax = new Map<number, number>();
    let max = 0;
    for (const c of cells) {
      kSet.add(c.kitchen);
      fSet.add(c.front_of_register);
      map.set(`${c.kitchen}|${c.front_of_register}`, c.minutes);
      if (c.minutes > max) max = c.minutes;
      colTotal.set(c.front_of_register, (colTotal.get(c.front_of_register) ?? 0) + c.minutes);
      colMax.set(c.front_of_register, Math.max(colMax.get(c.front_of_register) ?? 0, c.minutes));
    }
    return {
      colTotal,
      colMax,
      // Kitchen rows DESCENDING so the smallest crew sits at the bottom —
      // reads like Y-axis values on a chart.
      rows: Array.from(kSet).sort((a, b) => b - a),
      cols: Array.from(fSet).sort((a, b) => a - b),
      maxMinutes: max,
      minutesByKey: map,
    };
  }, [cells]);

  const shareExample = useMemo(() => {
    let col: number | null = null;
    for (const [c, tot] of colTotal) if (c > 0 && (col == null || tot > (colTotal.get(col) ?? 0))) col = c;
    if (col == null) return null;
    let row: number | null = null;
    let best = 0;
    for (const r of rows) {
      const m = minutesByKey.get(`${r}|${col}`) ?? 0;
      if (m > best) { best = m; row = r; }
    }
    const tot = colTotal.get(col) ?? 0;
    if (row == null || tot <= 0) return null;
    return { col, row, pct: Math.round((best / tot) * 100) };
  }, [colTotal, rows, minutesByKey]);

  if (!cells.length) return null;

  return (
    <MetricCard icon={UsersRound} title={label}
      hint="Сколько минут за период у кассы стояло N гостей при M сотрудниках за баром. Темнее — дольше. Нажми на клетку — покажем кадры этих минут (только пары кадров, снятые одновременно)."
      right={avgRatio ? <span className="text-xs tabular-nums text-muted-foreground"><span className="font-semibold text-foreground">{avgRatio.perWorker.toFixed(1)}</span> гостя на сотрудника в среднем</span> : undefined}>
        <div className="mb-2 flex flex-wrap items-center gap-1">
          {(['share', ...(multiDay ? ['perDay'] : []), 'total'] as WorkerMatrixMode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => pickMode(m)}
              className={
                m === effMode
                  ? 'rounded-md bg-primary px-2 py-0.5 text-[11px] font-medium text-primary-foreground'
                  : 'rounded-md bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground'
              }
            >
              {/* «доля в столбце» / «минуты» были непонятны (Алексей 04.11 #4081). */}
              {m === 'share' ? '% времени' : m === 'perDay' ? 'минут в день' : multiDay ? 'минут за период' : 'минут за день'}
            </button>
          ))}
        </div>
        {/* O2: the matrix STRETCHES to its panel — cells flex-grow so no
            blank space remains to the right. */}
        <div className="flex flex-1 items-end gap-2 overflow-x-auto pb-1" onMouseLeave={() => setHover(null)}>
          <div
            className="shrink-0 self-center text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
          >
            {kitchenLabel} ↑
          </div>
          <div className="min-w-0 flex-1">
            <div className="space-y-0.5">
              {rows.map((row) => (
                <div key={row} className="flex items-center gap-0.5">
                  <div className="w-5 shrink-0 text-right text-[10px] tabular-nums text-muted-foreground">{row}</div>
                  {cols.map((col) => {
                    const m = minutesByKey.get(`${row}|${col}`) ?? 0;
                    const cTot = colTotal.get(col) ?? 0;
                    const cMax = colMax.get(col) ?? 0;
                    const intensity =
                      effMode === 'share' ? (cMax > 0 ? m / cMax : 0) : maxMinutes > 0 ? m / maxMinutes : 0;
                    const shown =
                      m <= 0
                        ? ''
                        : effMode === 'share'
                          ? `${Math.round((m / Math.max(cTot, 1)) * 100)}%`
                          : effMode === 'perDay'
                            ? fmtCellMin(m / perDayDivisor)
                            : String(m);
                    return (
                      <div
                        key={col}
                        className={cn(
                          'flex h-8 min-w-8 flex-1 items-center justify-center rounded-sm text-[10px] tabular-nums',
                          m > 0 && 'cursor-pointer hover:ring-1 hover:ring-primary/60',
                        )}
                        style={{
                          backgroundColor:
                            intensity === 0
                              ? 'var(--color-muted)'
                              : `rgba(99, 102, 241, ${0.15 + intensity * 0.7})`,
                          color: intensity > 0.6 ? 'white' : undefined,
                        }}
                        onMouseMove={(e) => setHover({ x: e.clientX, y: e.clientY, row, col, m })}
                        onClick={() => {
                          if (m > 0) {
                            setExamplesCell({ row, col });
                            setExampleIdx(0);
                          }
                        }}
                      >
                        {shown}
                      </div>
                    );
                  })}
                </div>
              ))}
              <div className="flex items-center gap-0.5">
                <div className="w-5 shrink-0" />
                {cols.map((col) => (
                  <div key={col} className="min-w-8 flex-1 text-center text-[10px] tabular-nums text-muted-foreground">
                    {col}
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-1 text-center text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{frontLabel} →</div>
          </div>
        </div>
        <div className="mt-1 text-[10px] text-muted-foreground">
          {/* «50% столбца — ваще непонятно что значит» (Алексей 04.11 #4081): объясняем на
              живом примере из этой же матрицы. */}
          {effMode === 'share'
            ? shareExample
              ? `Как читать: ${shareExample.pct}% в клетке «у кассы ${shareExample.col} · за баром ${shareExample.row}» — из всего времени, когда у кассы было ${shareExample.col} гост., ${shareExample.pct}% времени за баром стояло ${shareExample.row} сотр. Каждый столбец в сумме — 100%.`
              : 'Как читать: из всего времени, когда у кассы было N гостей (столбец), какую долю за баром стояло M сотрудников (строка). Каждый столбец в сумме — 100%.'
            : effMode === 'perDay'
              ? 'В клетке — сколько минут в среднем за день у кассы было N гостей (столбец), а за баром M сотрудников (строка)'
              : `В клетке — сколько минут ${multiDay ? 'за период' : 'за день'} у кассы было N гостей (столбец), а за баром M сотрудников (строка)`}
        </div>
        {hover && (
          <div
            className="pointer-events-none fixed z-50 rounded-lg border border-border bg-card px-2 py-1 text-xs text-foreground shadow-md"
            style={
              hover.x > window.innerWidth - 260
                ? { right: window.innerWidth - hover.x + 12, top: hover.y + 12 }
                : { left: hover.x + 12, top: hover.y + 12 }
            }
          >
            <div>
              {kitchenLabel}: <span className="font-medium tabular-nums">{hover.row}</span> · {frontLabel}:{' '}
              <span className="font-medium tabular-nums">{hover.col}</span>
            </div>
            <div className="text-muted-foreground">
              {fmtMinutes(hover.m)}
              {multiDay ? ` за период (${fmtMinutes(Math.round(hover.m / perDayDivisor))} в день)` : ''}
              {' · '}
              {Math.round((hover.m / Math.max(colTotal.get(hover.col) ?? 0, 1)) * 100)}% всего времени, когда у кассы было {hover.col} гост.
            </div>
            <div className="text-muted-foreground">нажмите — примеры кадров
            </div>
          </div>
        )}
        {/* ImageLightbox freezes its props the moment it mounts (so a live
            data refresh underneath an open gallery can't blink the photo) —
            so the boxes fetch must resolve BEFORE the lightbox opens, or it'd
            freeze on an empty boxesList and never pick up the real boxes. */}
        {/* The gallery overlay appears IMMEDIATELY on click — with a loader
            while examples (and, with the boxes checkbox on, their boxes) are
            fetched, and an explicit empty state — instead of a easily-missed
            status line under the chart. */}
        {examplesCell && (examplesLoading || (examples && examples.length === 0) || boxesPending || (!examples && examplesError)) && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            onClick={() => setExamplesCell(null)}
          >
            <div
              className="flex h-40 w-full max-w-sm flex-col items-center justify-center gap-2 rounded-xl bg-card text-sm text-muted-foreground"
              onClick={(e) => e.stopPropagation()}
            >
              {examplesLoading || (examples && examples.length > 0) ? (
                <>
                  <Loader2 className="size-5 animate-spin" aria-hidden />
                  {examplesLoading ? 'Ищем примеры…' : 'Загружаем боксы…'}
                </>
              ) : !examples && examplesError ? (
                <span className="px-4 text-center">Не удалось загрузить примеры — попробуйте ещё раз чуть позже</span>
              ) : (
                <span className="px-4 text-center">
                  Примеров за период нет
                  <span className="mt-1 block text-xs">нет сохранённых кадров, где у кассы {examplesCell.col}, а за баром {examplesCell.row}</span>
                </span>
              )}
            </div>
          </div>
        )}
        {examplesCell && examples && examples.length > 0 && !boxesPending && (
          <ImageLightbox
            srcs={examples.map((e) => e.image_url)}
            // examples[].image_url IS the full camera frame (not a polygon
            // crop) — reusing it as frameSrcs just switches the lightbox into
            // full-frame mode so the boxes overlay has somewhere to draw;
            // omitted entirely (not just empty) when the checkbox is off, so
            // behavior is byte-for-byte the pre-#158 plain gallery.
            frameSrcs={showBoxes ? examples.map((e) => e.image_url) : undefined}
            boxesList={showBoxes ? examples.map((e) => boxesByFrame?.[e.frame_id] ?? []) : undefined}
            // Очередь и бар с разных камер — два кадра рядом, у каждого своё
            // время с секундами: разрыв между ними и есть то, что надо видеть.
            pairSrcs={examples.map((e) => e.pair?.image_url ?? null)}
            pairLabels={examples.map((e) => (e.pair
              ? [`${t('analytics.queue.area.queue')} · ${fmtClock(e.at)}`, `${kitchenLabel} · ${fmtClock(e.pair.at)}`]
              : null))}
            index={exampleIdx}
            alt=""
            captions={examples.map((e) => {
              const at = new Date(e.at).toLocaleString('ru-RU', {
                day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
              });
              return `${kitchenLabel}: ${examplesCell.row} · ${frontLabel}: ${examplesCell.col} · ${at}`;
            })}
            onClose={() => setExamplesCell(null)}
            onNavigate={setExampleIdx}
          />
        )}
    </MetricCard>
  );
}

/** «12:31:05» — время кадра с секундами, для пары кадров с разных камер. */
function fmtClock(iso: string): string {
  return new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

/** "2 ч 15 мин" / "45 мин" / "40 с" — durations people can act on. */
function fmtSecs(seconds: number): string {
  if (seconds < 90) return `${Math.round(seconds)} с`;
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} мин`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest === 0 ? `${h} ч` : `${h} ч ${rest} мин`;
}

/** Время в очереди — своим запросом: queue-detail отдаёт `wait_time` пустым (поминутный
 *  ряд убрали ради скорости), а агрегат идёт 0.7 с/день–5.3 с/неделю и не должен держать
 *  график выше. */
function WaitTimeCard({ zoneId, from, to }: { zoneId: string; from: string; to: string }) {
  const { data: waitData, isLoading: waitLoading } = useSWR(
    zoneId ? ['queue:wait-summary', zoneId, from, to] : null,
    () => getQueueWaitSummary(zoneId, from, to).then((r) => r.data),
    { revalidateOnFocus: false },
  );
  const wait =
    waitData && waitData.avg_seconds != null && waitData.samples > 0
      ? { avg: waitData.avg_seconds, max: waitData.max_seconds ?? waitData.avg_seconds }
      : null;
  // «в зоне ожидания» = ORDER_WAITING polygon (Grafana window 40); the card is
  // shown only when the zone actually has an order-waiting polygon (Alexey 2026-08-25).
  const waitWaiting =
    waitData?.waiting && waitData.waiting.avg_seconds != null && waitData.waiting.samples > 0
      ? { avg: waitData.waiting.avg_seconds, max: waitData.waiting.max_seconds ?? waitData.waiting.avg_seconds }
      : null;
  return (
    <MetricCard icon={Hourglass} title="Время в очереди" hint="Сколько гость стоит в очереди к кассе: оценка по кадрам — средняя и самая долгая за период. Ниже — то же для зоны ожидания заказа, если она размечена.">
      {wait ? (
        <MetricRow>
          <MetricBig value={fmtSecs(wait.avg)} label="в среднем" className="text-foreground" />
          <MetricSmall value={fmtSecs(wait.max)} label="максимум" />
        </MetricRow>
      ) : (
        <div className="text-xs text-muted-foreground">{waitLoading ? 'Считаем…' : 'Нет данных за период.'}</div>
      )}
      {waitWaiting && (
        <>
          <div className="mb-1.5 mt-3 text-[11px] font-medium text-muted-foreground">Время в зоне ожидания</div>
          <MetricRow>
            <MetricSmall value={fmtSecs(waitWaiting.avg)} label="в среднем" className="text-foreground" />
            <MetricSmall value={fmtSecs(waitWaiting.max)} label="максимум" />
          </MetricRow>
        </>
      )}
    </MetricCard>
  );
}

/** Сколько человек в зоне: среднее по времени, пик и распределение «сколько минут при
 *  длине N». Одна карточка на зону — «Размер очереди» (очередь / ожидают) в ряду
 *  «Очереди» и «За баром» (кухня) в ряду «Сотрудники», ровно под ней (Алексей 22.10, #1850).
 *
 *  Размер — взвешенный по времени из гистограммы, не среднее по кадрам: 10 минут по
 *  8 человек не должны весить как 10 секунд. «Никого» входит в среднее нарочно.
 *  «Никого» показываем только для очередей: у бара это значило бы «никто не работал» —
 *  другое утверждение (Алексей: «только для очередей, не для бара»). */
function AreaSizeCard({
  hist,
  areas = [],
  area,
  onAreaChange,
  areaLabel,
  perDayDivisor,
  title,
  hint,
  chartLabel,
}: {
  hist: QueueAreaHistogram;
  areas?: QueueArea[];
  area: QueueArea;
  onAreaChange?: (a: QueueArea) => void;
  areaLabel?: (a: QueueArea) => string;
  /** Effective days in the range — «Никого» is reported per day on multi-day. */
  perDayDivisor: number;
  title: string;
  hint: string;
  chartLabel: string;
}) {
  const color = AREA_COLORS[area];
  const size = useMemo(() => {
    const all = [
      { people: 0, minutes: hist.idle_minutes },
      ...hist.buckets.filter((b) => b.people > 0),
    ];
    const totalMin = all.reduce((a, b) => a + b.minutes, 0);
    if (totalMin <= 0) return null;
    const weighted = all.reduce((a, b) => a + b.people * b.minutes, 0);
    const max = all.reduce((m, b) => (b.minutes > 0 && b.people > m ? b.people : m), 0);
    return { avg: weighted / totalMin, max, hist: all.sort((a, b) => a.people - b.people) };
  }, [hist]);
  const perDay = perDayDivisor > 1.2;
  const idleShown = perDay ? hist.idle_minutes / perDayDivisor : hist.idle_minutes;
  const showIdle = area !== 'kitchen';

  return (
    <MetricCard icon={Users} title={title} hint={hint}
      right={areas.length > 1 && onAreaChange && areaLabel ? (
        <div className="flex items-center gap-1">
          {areas.map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => onAreaChange(a)}
              className={
                a === area
                  ? 'rounded-md px-2 py-0.5 text-[11px] font-medium text-white'
                  : 'rounded-md bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground'
              }
              style={a === area ? { background: AREA_COLORS[a] } : undefined}
            >
              {areaLabel(a)}
            </button>
          ))}
        </div>
      ) : undefined}>
      {size ? (
        <>
          <MetricRow>
            <MetricBig value={size.avg.toFixed(1)} unit="чел." label="в среднем" className="text-foreground" />
            <MetricSmall value={size.max} unit="чел." label="максимум" />
            {showIdle && <MetricSmall value={fmtMinutes(Math.round(idleShown))} label={`никого${perDay ? ' · в день' : ''}`} />}
          </MetricRow>
          {/* Распределение: сколько минут длина была N. Подписи оси — с запасом по высоте и
              краям: на 14 px они обрезались снизу и по бокам (Алексей 22.10, #1850). */}
          <div className="mt-3 text-[11px] text-muted-foreground">{chartLabel}</div>
          <div className="mt-1 h-[92px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={size.hist} margin={{ top: 2, right: 6, bottom: 2, left: 6 }} barCategoryGap={2}>
                <XAxis dataKey="people" type="category" tickLine={false} axisLine={false} height={20} tickMargin={4} tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }} interval="preserveStartEnd" minTickGap={2} />
                <Tooltip
                  isAnimationActive={false}
                  cursor={{ fill: 'var(--color-muted)', fillOpacity: 0.5 }}
                  contentStyle={{
                    fontSize: 12,
                    borderRadius: 8,
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-card)',
                    color: 'var(--color-foreground)',
                  }}
                  formatter={(v) => [fmtMinutes(Math.round(v as number)), '']}
                  labelFormatter={(l) => `${l} чел.`}
                />
                <Bar dataKey="minutes" fill={color} fillOpacity={0.45} radius={[3, 3, 0, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      ) : (
        <div className="pb-3 text-xs text-muted-foreground">Нет данных за период.</div>
      )}
    </MetricCard>
  );
}

function atFloor(cell: QueueDwellCell, seconds: number): boolean {
  return cell.floor_seconds != null && seconds <= cell.floor_seconds;
}

function QueueDwellSummary({ cells, hist, idleMinutes }: { cells: QueueDwellCell[]; hist: QueueHistogramBucket[]; idleMinutes: number }) {
  if (!cells.length) return null;
  const maxEp = Math.max(1, ...cells.map((c) => c.episodes));
  // Сводка внизу (Алексей 20.10, AI286): рабочих часов за период и сколько из них у кассы
  // кто-то стоял. Минуты с людьми — из гистограммы длины очереди (people ≥ 1), рабочие —
  // они же плюс минуты «никого» (idle_minutes), поэтому нулевой бакет не считаем дважды.
  const busyMin = hist.filter((b) => b.people >= 1).reduce((n, b) => n + b.minutes, 0);
  const workMin = busyMin + Math.max(0, idleMinutes);
  const busyPct = workMin > 0 ? Math.round((100 * busyMin) / workMin) : null;
  return (
    <MetricCard icon={Clock} title="Сколько стоит очередь"
      hint="Среднее время ОДНОГО раза: очередь такого размера собралась и разошлась. Рядом — сколько раз за период это было. Очередь, которая пришла и ушла между двумя кадрами, измерить нельзя, поэтому такие значения помечены «меньше».">
      {/* Одна строка — одно предложение: «очередь 3 чел. в среднем стоит 1 мин 18 с, и так
          было 177 раз» (Алексей 11.09). Медиана не показывается: на наших данных она почти
          всегда равна интервалу съёмки. Полоска справа — как часто бывало, относительно. */}
      {/* Колонки по содержимому и без переноса: «В очереди», «В сумме» и «1 ч 5 мин» ломались
          в узких фиксированных колонках при свободном месте (Алексей 22.10, #1850). */}
      {/* Шапка и строки — одна сетка: в двух отдельных сетках auto-колонки считались порознь,
          шапка «Раз за период» уезжала за край карточки (#1850). */}
      <div className="grid min-h-0 flex-1 grid-cols-[auto_1fr_auto_auto] content-start items-center gap-x-3 gap-y-1.5 whitespace-nowrap">
        <span className="pb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground/70">В очереди</span>
        <span className="pb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground/70">Стоит в среднем</span>
        <span className="pb-0.5 text-right text-[10px] uppercase tracking-wide text-muted-foreground/70">В сумме</span>
        <span className="pb-0.5 text-right text-[10px] uppercase tracking-wide text-muted-foreground/70">Раз</span>
        {cells.map((c) => {
          const secs = c.mean_seconds ?? c.median_seconds;
          const floor = atFloor(c, secs);
          const total = c.total_seconds ?? c.episodes * secs;
          return (
            <div key={c.length} className="contents text-sm">
              <span className="tabular-nums text-muted-foreground">{c.length} чел.</span>
              <span className={cn('font-semibold tabular-nums', floor && 'font-normal text-muted-foreground')}>{floor ? `меньше ${fmtSecs(secs)}` : fmtSecs(secs)}</span>
              <span className="text-right tabular-nums">{fmtSecs(total)}</span>
              <span className="flex items-center justify-end gap-1.5 tabular-nums text-muted-foreground">
                <span className="h-1.5 w-10 overflow-hidden rounded-full bg-muted"><span className="block h-full rounded-full bg-primary/40" style={{ width: `${Math.max(4, (c.episodes / maxEp) * 100)}%` }} /></span>
                {c.episodes}
              </span>
            </div>
          );
        })}
      </div>
      {workMin > 0 && (
        // Итог под таблицей: аккуратная полоса-подпись, не график и не plain text (AI286).
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 rounded-md border border-dashed bg-muted/40 px-3 py-1.5 text-[11px] text-muted-foreground">
          <span>за период рабочих часов <b className="font-semibold tabular-nums text-foreground">{fmtMinutes(workMin)}</b></span>
          <span>с людьми у кассы <b className="font-semibold tabular-nums text-foreground">{fmtMinutes(busyMin)}</b>{busyPct != null && <span className="ml-1 rounded bg-primary/10 px-1 py-px tabular-nums text-primary">{busyPct}%</span>}</span>
        </div>
      )}
    </MetricCard>
  );
}

/** «Нагрузка на сотрудника» — третья карточка ряда «Сотрудники» (Алексей 22.10, #1861: пустой ряд).
 *  Из той же матрицы «за баром × у кассы»: сколько гостей на одного за баром и сколько времени
 *  рук не хватало (очередь от порога, за баром один или никого) или было с запасом. */
function StaffLoadCard({ cells, threshold, perDayDivisor }: { cells: QueueDetailResponse['worker_matrix']; threshold: number; perDayDivisor: number }) {
  const v = useMemo(() => {
    let minutes = 0, guests = 0, crew = 0, short = 0, idle = 0;
    for (const c of cells) {
      if (c.front_of_register >= threshold && c.kitchen <= 1) short += c.minutes;
      if (c.front_of_register === 0 && c.kitchen >= 3) idle += c.minutes;
      if (c.kitchen <= 0) continue;
      minutes += c.minutes; guests += c.front_of_register * c.minutes; crew += c.kitchen * c.minutes;
    }
    return minutes > 0 && crew > 0 ? { perWorker: guests / crew, short, idle } : null;
  }, [cells, threshold]);
  if (!v) return null;
  const perDay = perDayDivisor > 1.2;
  const d = (m: number) => fmtMinutes(Math.round(perDay ? m / perDayDivisor : m));
  return (
    <MetricCard icon={UsersRound} title="Нагрузка на сотрудника"
      hint={`Гостей у кассы на одного сотрудника за баром — среднее по времени. «Не хватало рук» — у кассы от ${threshold} гостей, а за баром один или никого; «с запасом» — у кассы никого, а за баром трое и больше.`}>
      <MetricRow>
        <MetricBig value={v.perWorker.toFixed(1)} unit="гостя" label="на сотрудника" className="text-foreground" />
        <MetricSmall value={d(v.short)} label={`не хватало рук${perDay ? ' · в день' : ''}`} className={v.short / Math.max(perDayDivisor, 1) >= 30 ? 'text-amber-700 dark:text-amber-300' : undefined} />
        <MetricSmall value={d(v.idle)} label={`с запасом${perDay ? ' · в день' : ''}`} />
      </MetricRow>
    </MetricCard>
  );
}

/** Приход выключен — вместо пустой ячейки приглашение включить (#1861). */
function ArrivalTeaser() {
  return (
    <Link to="/management/queues-staff" className="flex h-full flex-col justify-center gap-1 rounded-xl border border-dashed border-border bg-card p-4 transition-colors hover:border-primary/50 hover:bg-muted/40">
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><Sunrise className="size-3.5" />Приход к открытию</div>
      <div className="text-sm text-muted-foreground">Не включён. Покажет, во сколько первые сотрудники за баром относительно открытия.</div>
      <div className="text-xs font-medium text-primary">Включить →</div>
    </Link>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 pt-2">
      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{children}</span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

function TabLoading() {
  return (
    <div className="space-y-3">
      <div className="h-56 rounded bg-muted animate-pulse" />
      <div className="h-32 rounded bg-muted animate-pulse" />
      <div className="h-24 rounded bg-muted animate-pulse" />
    </div>
  );
}

/** Bucket width for the signature queue chart over an arbitrary range.
 *  Stays INTRA-DAY as long as possible — the day rhythm is the whole point —
 *  only degrading to coarse buckets past ~5 weeks. */
// Bars per day matter more than total bars: 3 days at 30 min gave ~28 bars a day
// — «супер неинформативно» (Alexey 13.10 #5810, not the first time). Keep the
// working day readable (~80+ bars) up to a week.
export function queueBucketMs(from: string, to: string): number {
  const days = Math.max(
    1,
    Math.round((new Date(to).getTime() - new Date(from).getTime()) / 86400000) + 1,
  );
  if (days <= 1) return 5 * 60 * 1000;
  if (days <= 3) return 10 * 60 * 1000;
  if (days <= 7) return 15 * 60 * 1000;
  if (days <= 14) return 30 * 60 * 1000;
  if (days <= 35) return 60 * 60 * 1000;
  return 2 * 60 * 60 * 1000;
}

export default function AnalyticsQueueDetailTab({
  zoneId,
  from,
  to,
  timeWindow,
  onTimeWindow,
}: {
  zoneId: string;
  from: string;
  to: string;
  /** Sub-period from the page (?time=) — the marquee sets it, like «Выкладка». */
  timeWindow?: [number, number] | null;
  onTimeWindow?: (w: [number, number] | null) => void;
}) {
  const { t } = useT();
  const [area, setArea] = useState<QueueArea>('queue');
  const { data, error, isLoading } = useSWR(
    zoneId ? ['analytics:queue-detail', zoneId, from, to] : null,
    // 'peak' → the collage shows the busiest (longest-queue) moments, not an
    // even spread that kept surfacing 0-people calm frames (Alexey 2026-07-21).
    () => getQueueDetail(zoneId, from, to, undefined, { photoMode: 'peak' }).then((r) => r.data),
    // No focus-revalidation — a refetch on refocus reflows the chart/photo panel.
    { revalidateOnFocus: false },
  );
  // Ряд «Сотрудники» (#1861): выключен ли приход (тот же ключ, что у карточки прихода — один запрос)
  // и порог очереди для «не хватало рук» — тот же, что линия порога на графике.
  const { data: arrival } = useSWR(zoneId ? ['staff-arrival', zoneId, from, to] : null, () => getStaffArrival(zoneId, from, to).then((r) => r.data), { revalidateOnFocus: false });
  const { data: qs } = useSWR(zoneId ? queuesStaffKey(zoneId) : null, () => getQueuesStaffSettings(zoneId).then((r) => r.data), { revalidateOnFocus: false });
  const queueThreshold = qs?.queues.length ? Math.min(...qs.queues.map((q) => q.max_allowed_size)) : 4;

  if (isLoading && !data) return <TabLoading />;
  if (error && !data) {
    return (
      <Card>
        <CardContent className="p-0">
          <ServerStatusNotice variant="card" error={error} />
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;
  if (!data.configured) {
    return (
      <Card>
        <CardContent className="flex h-36 items-center justify-center text-sm text-muted-foreground">
          {t('analytics.queue.notConfigured')}
        </CardContent>
      </Card>
    );
  }

  const hasKitchen = data.polygons.kitchen.length > 0;

  // Per-area histograms drive the Очередь/Кухня/Ожидают toggle (one card,
  // like before — the side-by-side trio read worse). Falls back to the
  // legacy single queue histogram if the backend predates area_histograms.
  const areaHistograms = data.area_histograms ?? [
    { area: 'queue' as QueueArea, buckets: data.length_histogram, idle_minutes: data.idle_minutes },
  ];
  // Кухня (бар) уехала в ряд «Сотрудники» (#1850): в переключателе очереди её нет.
  const queueAreas: QueueArea[] = areaHistograms.map((a) => a.area).filter((a) => a !== 'kitchen');
  const selectedArea = queueAreas.includes(area) ? area : 'queue';
  const currentHist = areaHistograms.find((a) => a.area === selectedArea) ?? areaHistograms[0];
  const kitchenHist = areaHistograms.find((a) => a.area === 'kitchen');

  // Effective (fractional) days in the range: the idle readout divides by
  // this, counting today only up to NOW — half-days count as halves.
  const rangeStart = new Date(`${from}T00:00:00`).getTime();
  const rangeEnd = Math.min(new Date(`${to}T23:59:59`).getTime(), Date.now());
  const effectiveDays = Math.max((rangeEnd - rangeStart) / 86400000, 1 / 24);

  return (
    <div className="space-y-4">
      {/* The signature queue chart: bars + cashier/bar lines, photos of the
          period's peaks on top, fullscreen + marquee zoom. Replaces the old
          overlay line chart and the longest-queue photo collage — the chart
          itself surfaces those moments. */}
      <DayQueueCard
        zoneId={zoneId}
        day={from}
        to={to}
        bucketMs={queueBucketMs(from, to)}
        title={t('analytics.tab.queue')}
        zoom={timeWindow ?? null}
        onZoom={onTimeWindow}
      />

      {/* Два раздела по смыслу: «Очереди» — гости у кассы, «Сотрудники» — команда за баром
          (Алексей 22.10, #1850; было «Сводные показатели» / «Зависимости»). Ряды на одной
          сетке: «За баром» стоит ровно под «Размером очереди». */}
      <SectionHeader>Очереди</SectionHeader>
      <div className="grid items-stretch gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <WaitTimeCard zoneId={zoneId} from={from} to={to} />
        <AreaSizeCard
          hist={currentHist}
          areas={queueAreas}
          area={selectedArea}
          onAreaChange={setArea}
          areaLabel={(a) => t(`analytics.queue.area.${a}`)}
          perDayDivisor={effectiveDays}
          title="Размер очереди"
          hint="Сколько человек стоит у кассы: среднее по времени и пик за период; «никого» — сколько времени у кассы не было ни одного гостя. Переключатель — то же для зоны ожидания."
          chartLabel="сколько минут при длине очереди, чел."
        />
        <QueueDwellSummary cells={data.dwell_by_length ?? []} hist={data.length_histogram ?? []} idleMinutes={data.idle_minutes ?? 0} />
      </div>

      <SectionHeader>Сотрудники</SectionHeader>
      <div className="grid items-stretch gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {/* Приход выключен — приглашение включить: «За баром» остаётся под «Размером очереди» (#1861). */}
        <div className="h-full">{arrival?.enabled === false ? <ArrivalTeaser /> : <StaffArrivalSummaryCard zoneId={zoneId} from={from} to={to} />}</div>
        {kitchenHist && (
          <AreaSizeCard
            hist={kitchenHist}
            area="kitchen"
            perDayDivisor={effectiveDays}
            title="За баром"
            hint="Сколько сотрудников за баром: среднее по времени и пик за период, ниже — сколько минут их было столько."
            chartLabel="сколько минут при стольких сотрудниках, чел."
          />
        )}
        {hasKitchen && <StaffLoadCard cells={data.worker_matrix} threshold={queueThreshold} perDayDivisor={effectiveDays} />}
      </div>
      {hasKitchen && (
        <WorkerMatrix
          cells={data.worker_matrix}
          label={t('analytics.queue.workerMatrixLabel')}
          kitchenLabel={t('analytics.queue.workerMatrixKitchen')}
          frontLabel={t('analytics.queue.workerMatrixFront')}
          zoneId={zoneId}
          from={from}
          to={to}
          perDayDivisor={effectiveDays}
        />
      )}
    </div>
  );
}
